import { Color, Spacing } from '@persona-id/design-tokens';
import { MinusIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import styled, { ThemeProvider } from 'styled-components';

import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import { darkTheme } from 'design-system/theme';
import {
  useKeydownZoomInListener,
  useKeydownZoomOutListener,
  useKeydownZoomResetListener,
} from 'lib/hooks/useKeyListener';

interface DocumentViewerZoomProps {
  setZoomScale: (zoomScale: number) => void;
  zoomScale: number;
  incrementZoom: () => void;
  decrementZoom: () => void;
  zoomVisualValue: string;
  setZoomVisualValue: (zoomVisualValue: string) => void;
}

const HeaderButtonContainer = styled('div')`
  display: flex;
  align-items: center;
`;
const HeaderZoomInput = styled(Input)`
  width: 80px;
  margin: 0px ${Spacing[2]};
  text-align: center;
`;
const HeaderStyledButton = styled(Button)<{ isShown?: boolean }>`
  background-color: ${({ isShown }) => (isShown ? Color.PrimaryWhite : Color.CoreSlate900)};
  margin: 0px ${Spacing[2]};
  cursor: pointer;
  svg path {
    ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
  }
  // Override Button svg stroke style
  svg.pds-icon {
    path {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }
    path[stroke] {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }
  }
`;
const DocumentViewerRotateZoom = ({
  setZoomScale,
  zoomScale,
  incrementZoom,
  decrementZoom,
  zoomVisualValue,
  setZoomVisualValue,
}: DocumentViewerZoomProps) => {
  useKeydownZoomOutListener({ stopPropagation: true })(decrementZoom);
  useKeydownZoomInListener({ stopPropagation: true })(incrementZoom);
  useKeydownZoomResetListener({ stopPropagation: true })(() => {
    setZoomScale(1);
  });
  const onZoomVisualValueChange = (zoomVisualValue: string) => {
    const zoomNumericValue = Math.round(parseInt(zoomVisualValue, 10));
    if (isNaN(zoomNumericValue)) {
      // ignore invalid value
      setZoomScale(zoomScale);
    } else {
      setZoomScale(zoomNumericValue / 100);
    }
  };
  return (
    <ThemeProvider theme={darkTheme}>
      <HeaderButtonContainer>
        <HeaderStyledButton
          data-test='document-viewer__header--decrement-zoom-button'
          id='document-viewer__header--decrement-zoom-button'
          onClick={() => {
            decrementZoom();
          }}
          width='icon'
        >
          <MinusIcon />
        </HeaderStyledButton>
        <HeaderZoomInput
          onChange={(e) => {
            setZoomVisualValue(e.target.value);
          }}
          onBlur={() => {
            onZoomVisualValueChange(zoomVisualValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onZoomVisualValueChange(zoomVisualValue);
            }
          }}
          value={zoomVisualValue}
        />
        <HeaderStyledButton
          data-test='document-viewer__header--increment-zoom-button'
          id='document-viewer__header--increment-zoom-button'
          onClick={() => {
            incrementZoom();
          }}
          width='icon'
        >
          <PlusIcon />
        </HeaderStyledButton>
      </HeaderButtonContainer>
    </ThemeProvider>
  );
};
export default DocumentViewerRotateZoom;
